import {FetchWrapper} from '@/ts/api/fetch-wrapper';
import {
  BACKEND_ADDRESS,
  ZITADEL,
} from '@/ts/utils/consts';
import {PublicApi} from '@/ts/api/modules/public-api';
import {MeApi} from '@/ts/api/modules/me-api';
import {Language} from '@/ts/types/component/localization.type';
import {ZitadelApi} from '@/ts/api/modules/zitadel-api';
import type {ZitadelGetTokenResponse} from '@/ts/types/dto/zitadel-dto';
import type {LogMethods} from '@significo/pulse';
import {MetricApi} from '@/ts/api/modules/metric-api';
import {ContentApi} from '@/ts/api/modules/content-api';
import {AdminApi} from '@/ts/api/modules/admin-api';
import {SignUpApi} from '@/ts/api/modules/sign-up-api';
import {AppError} from '@/ts/utils/errors';
import {AppErrorCode} from '@/ts/types/dto/error-codes-dto';

/** Composes all API modules (public, zitadel, me, metric, content) with shared auth and language headers */
export class Api {
  public readonly public: PublicApi;
  public readonly signUp: SignUpApi;
  public readonly zitadel: ZitadelApi;
  public readonly me: MeApi;
  public readonly metric: MetricApi;
  public readonly content: ContentApi;
  public readonly admin: AdminApi;

  // eslint-disable-next-line @typescript-eslint/max-params -- 5th param is optional token refresh callback
  constructor(
    private readonly getAccessToken: () => Promise<ZitadelGetTokenResponse | null>,
    private readonly getLanguage: () => Language,
    private readonly onLogout: () => Promise<void>,
    private readonly logger: LogMethods,
    private readonly onTokenRefresh?: () => Promise<ZitadelGetTokenResponse | null>,
  ) {
    const getLangHeader = (): Record<string, string> => ({
      'Accept-Language': { // eslint-disable-line @typescript-eslint/naming-convention
        [Language.German]: 'de-DE',
        [Language.English]: 'en-US',
      }[this.getLanguage()],
    });

    const getAuthHeaders = async(): Promise<Record<string, string>> => {
      const token: ZitadelGetTokenResponse | null = await this.getAccessToken();
      if (!token?.access_token) {
        throw new AppError('No auth token found', AppErrorCode.SESSION_EXPIRED);
      }
      return {
        Authorization: `${token.token_type} ${token.access_token}`,
        ...getLangHeader(),
      };
    };

    const forceRefreshHeaders = this.onTokenRefresh
      ? async(): Promise<Record<string, string> | null> => {
        const token = await this.onTokenRefresh!();
        if (!token?.access_token) {
          return null;
        }
        return {
          Authorization: `${token.token_type} ${token.access_token}`,
          ...getLangHeader(),
        };
      }
      : void 0;

    this.public = new PublicApi(
      new FetchWrapper(
        `${BACKEND_ADDRESS}/api/v1/public`,
        () => getLangHeader(),
        Promise.resolve,
        this.logger,
      ),
    );
    // Same `/public` prefix as `this.public`, but sending the bearer token — and two wrappers,
    // because the two endpoints want opposite things from a 401. The probe's 401 is an answer the
    // caller has to see, so it must not tear the session down; complete-signup's is an ordinary
    // dead session, and swallowing it strands the user on the consent step with a toast and no
    // way out. (`async() => {}` rather than `Promise.resolve`: the handler is invoked as
    // `this.onAccessDenied()`, and Promise.resolve needs a constructor for `this`.)
    this.signUp = new SignUpApi(
      new FetchWrapper(
        `${BACKEND_ADDRESS}/api/v1/public`,
        getAuthHeaders,
        async() => {},
        this.logger,
        forceRefreshHeaders,
      ),
      new FetchWrapper(
        `${BACKEND_ADDRESS}/api/v1/public`,
        getAuthHeaders,
        this.onLogout,
        this.logger,
        forceRefreshHeaders,
      ),
    );

    this.zitadel = new ZitadelApi(
      new FetchWrapper(
        ZITADEL.issuer,
        async() => Promise.resolve({}),
        Promise.resolve,
        this.logger,
      ),
    );

    this.me = new MeApi(
      new FetchWrapper(
        `${BACKEND_ADDRESS}/api/v1/me`,
        getAuthHeaders,
        this.onLogout,
        this.logger,
        forceRefreshHeaders,
      ),
    );

    this.content = new ContentApi(
      new FetchWrapper(
        `${BACKEND_ADDRESS}/api/v1/me`,
          getAuthHeaders,
          this.onLogout,
          this.logger,
          forceRefreshHeaders,
      ),
    );

    this.metric = new MetricApi(
      new FetchWrapper(
        `${BACKEND_ADDRESS}/api/v1/me`,
        getAuthHeaders,
        this.onLogout,
        this.logger,
        forceRefreshHeaders,
      ),
    );

    this.admin = new AdminApi(
      new FetchWrapper(
        `${BACKEND_ADDRESS}/api/v1/admin`,
        getAuthHeaders,
        this.onLogout,
        this.logger,
        forceRefreshHeaders,
      ),
    );
  }
}
